import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
    collection,
    getDocs,
    doc,
    setDoc,
    getDoc,
    updateDoc,
    query,
    where,
} from "firebase/firestore";
import {
    Warehouse,
    Store,
    Package,
    Plus,
    Minus,
    Save,
    Loader,
    AlertTriangle,
    Search,
    X,
    TrendingUp,
    TrendingDown,
} from "lucide-react";

const StockManager = () => {
    const [stores, setStores] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [storeInventory, setStoreInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (selectedStore) {
            fetchStoreInventory(selectedStore.id);
        }
    }, [selectedStore]);

    const fetchInitialData = async () => {
        try {
            // Cargar sucursales
            const storesSnap = await getDocs(collection(db, "stores"));
            const storesData = storesSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setStores(storesData);

            // Cargar productos
            const productsSnap = await getDocs(collection(db, "products"));
            const productsData = productsSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setProducts(productsData);

            // Seleccionar primera sucursal por defecto
            if (storesData.length > 0) {
                setSelectedStore(storesData[0]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            alert("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    const fetchStoreInventory = async (storeId) => {
        try {
            setLoading(true);
            const inventoryRef = doc(db, "storeInventory", storeId);
            const inventoryDoc = await getDoc(inventoryRef);

            if (inventoryDoc.exists()) {
                const inventoryData = inventoryDoc.data();
                // Combinar datos de inventario con datos de productos
                const enrichedInventory = products.map((product) => {
                    const stockData = inventoryData.products?.[product.id] || {};
                    return {
                        ...product,
                        stock: stockData.stock || 0,
                        customPrice: stockData.customPrice || product.basePrice,
                        minStock: stockData.minStock || 5,
                        isAvailable: stockData.isAvailable !== false,
                    };
                });
                setStoreInventory(enrichedInventory);
            } else {
                // No hay inventario, mostrar todos los productos con stock 0
                const defaultInventory = products.map((product) => ({
                    ...product,
                    stock: 0,
                    customPrice: product.basePrice,
                    minStock: 5,
                    isAvailable: true,
                }));
                setStoreInventory(defaultInventory);
            }
        } catch (error) {
            console.error("Error fetching inventory:", error);
            alert("Error al cargar el inventario");
        } finally {
            setLoading(false);
        }
    };

    const handleStockChange = (productId, change) => {
        setStoreInventory((prev) =>
            prev.map((item) =>
                item.id === productId
                    ? { ...item, stock: Math.max(0, item.stock + change) }
                    : item
            )
        );
    };

    const handlePriceChange = (productId, newPrice) => {
        setStoreInventory((prev) =>
            prev.map((item) =>
                item.id === productId
                    ? { ...item, customPrice: parseFloat(newPrice) || 0 }
                    : item
            )
        );
    };

    const handleMinStockChange = (productId, newMinStock) => {
        setStoreInventory((prev) =>
            prev.map((item) =>
                item.id === productId
                    ? { ...item, minStock: parseInt(newMinStock) || 0 }
                    : item
            )
        );
    };

    const handleAvailabilityChange = (productId, isAvailable) => {
        setStoreInventory((prev) =>
            prev.map((item) =>
                item.id === productId ? { ...item, isAvailable } : item
            )
        );
    };

    const handleSaveInventory = async () => {
        if (!selectedStore) {
            alert("Selecciona una sucursal primero");
            return;
        }

        setSaving(true);
        try {
            const inventoryRef = doc(db, "storeInventory", selectedStore.id);

            // Convertir array a objeto con productId como key
            const productsData = {};
            storeInventory.forEach((item) => {
                productsData[item.id] = {
                    stock: item.stock,
                    customPrice: item.customPrice,
                    minStock: item.minStock,
                    isAvailable: item.isAvailable,
                    productName: item.name,
                    productCategory: item.category,
                    lastUpdated: new Date().toISOString(),
                };
            });

            await setDoc(
                inventoryRef,
                {
                    storeId: selectedStore.id,
                    storeName: selectedStore.storeName,
                    products: productsData,
                    lastModified: new Date().toISOString(),
                },
                { merge: true }
            );

            alert("Inventario guardado exitosamente");
        } catch (error) {
            console.error("Error saving inventory:", error);
            alert("Error al guardar el inventario");
        } finally {
            setSaving(false);
        }
    };

    const filteredInventory = storeInventory.filter(
        (item) =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const lowStockProducts = storeInventory.filter(
        (item) => item.stock > 0 && item.stock <= item.minStock
    );

    const outOfStockProducts = storeInventory.filter((item) => item.stock === 0);

    if (loading && stores.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-[#FF0000] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando inventario...</p>
                </div>
            </div>
        );
    }

    if (stores.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-gray-500 mb-4">
                    No hay sucursales registradas. Crea una sucursal primero.
                </p>
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-gray-500 mb-4">
                    No hay productos registrados. Crea productos primero.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Gestión de Inventario</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Control de stock y precios por sucursal
                    </p>
                </div>
                <button
                    onClick={handleSaveInventory}
                    disabled={saving || !selectedStore}
                    className="bg-[#FF0000] text-white px-4 py-2.5 rounded-xl font-semibold hover:bg-[#CC0000] transition-all flex items-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? (
                        <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <Save className="w-5 h-5" strokeWidth={2} />
                            Guardar Cambios
                        </>
                    )}
                </button>
            </div>

            {/* Store Selector */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Seleccionar Sucursal
                </label>
                <select
                    value={selectedStore?.id || ""}
                    onChange={(e) => {
                        const store = stores.find((s) => s.id === e.target.value);
                        setSelectedStore(store);
                    }}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                >
                    {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                            {store.storeName}
                        </option>
                    ))}
                </select>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Total Productos</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {storeInventory.length}
                            </p>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-xl">
                            <Package className="w-6 h-6 text-blue-600" strokeWidth={2} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Stock Bajo</p>
                            <p className="text-2xl font-bold text-orange-600">
                                {lowStockProducts.length}
                            </p>
                        </div>
                        <div className="bg-orange-50 p-3 rounded-xl">
                            <AlertTriangle className="w-6 h-6 text-orange-600" strokeWidth={2} />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Sin Stock</p>
                            <p className="text-2xl font-bold text-red-600">
                                {outOfStockProducts.length}
                            </p>
                        </div>
                        <div className="bg-red-50 p-3 rounded-xl">
                            <TrendingDown className="w-6 h-6 text-red-600" strokeWidth={2} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-gray-400" strokeWidth={2} />
                    <input
                        type="text"
                        placeholder="Buscar producto..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 bg-transparent text-gray-800 text-sm font-medium placeholder-gray-400 focus:outline-none"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-gray-600" />
                        </button>
                    )}
                </div>
            </div>

            {/* Inventory Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                                    Producto
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                                    Categoría
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                                    Stock
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                                    Min. Stock
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                                    Precio
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                                    Disponible
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredInventory.length > 0 ? (
                                filteredInventory.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={`hover:bg-gray-50 ${item.stock === 0
                                                ? "bg-red-50/30"
                                                : item.stock <= item.minStock
                                                    ? "bg-orange-50/30"
                                                    : ""
                                            }`}
                                    >
                                        <td className="px-4 py-4">
                                            <div className="font-semibold text-gray-900">{item.name}</div>
                                            {item.stock === 0 && (
                                                <span className="text-xs text-red-600 font-semibold">
                                                    Sin stock
                                                </span>
                                            )}
                                            {item.stock > 0 && item.stock <= item.minStock && (
                                                <span className="text-xs text-orange-600 font-semibold">
                                                    Stock bajo
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="text-sm text-gray-600">{item.category}</span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => handleStockChange(item.id, -1)}
                                                    className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                >
                                                    <Minus className="w-4 h-4 text-gray-600" strokeWidth={2} />
                                                </button>
                                                <input
                                                    type="number"
                                                    value={item.stock}
                                                    onChange={(e) =>
                                                        setStoreInventory((prev) =>
                                                            prev.map((p) =>
                                                                p.id === item.id
                                                                    ? {
                                                                        ...p,
                                                                        stock: Math.max(
                                                                            0,
                                                                            parseInt(e.target.value) || 0
                                                                        ),
                                                                    }
                                                                    : p
                                                            )
                                                        )
                                                    }
                                                    className="w-16 text-center px-2 py-1 border border-gray-200 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                                />
                                                <button
                                                    onClick={() => handleStockChange(item.id, 1)}
                                                    className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                >
                                                    <Plus className="w-4 h-4 text-gray-600" strokeWidth={2} />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <input
                                                type="number"
                                                value={item.minStock}
                                                onChange={(e) =>
                                                    handleMinStockChange(item.id, e.target.value)
                                                }
                                                className="w-16 text-center px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <div className="flex flex-col items-center gap-1">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={item.customPrice}
                                                    onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                                    className="w-24 text-center px-2 py-1 border border-gray-200 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                                />
                                                {item.customPrice !== item.basePrice && (
                                                    <span className="text-xs text-gray-500">
                                                        Base: ${item.basePrice}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <label className="inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={item.isAvailable}
                                                    onChange={(e) =>
                                                        handleAvailabilityChange(item.id, e.target.checked)
                                                    }
                                                    className="w-5 h-5 text-[#FF0000] rounded focus:ring-[#FF0000]"
                                                />
                                            </label>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                        No se encontraron productos
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StockManager;


